import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api, type PresenceViewer } from './api';

// ─────────────────────────────────────────────────────────────────────────────
// usePresence — "who else is on this page" awareness (heartbeat + TTL, no
// websockets). Heartbeats AND polls the combined POST /presence/heartbeat
// endpoint every ~25s via react-query's refetchInterval — one round-trip
// both upserts the caller's own presence row and reads back the current
// co-viewers (self already excluded server-side — see presence.ts).
//
// PAUSED whenever the tab is hidden (Page Visibility API — same manual
// document.hidden + visibilitychange idiom lib/emberEffect.ts already uses
// to pause the ember canvas). The query is fully `enabled: false` while
// hidden, not just interval-paused, so a backgrounded tab makes zero
// requests; coming back to the foreground immediately re-enables and fires
// a fresh heartbeat.
//
// resourceKey is the PREFIXED id of whatever is being viewed (e.g.
// `COMP#abc123`, `ORG#anvil`), passed straight through to the backend in the
// request body — never the URL (see presence.ts's header for the '#'
// URL-fragment trap). Pass undefined to disable entirely (e.g. no
// competition selected yet).
// ─────────────────────────────────────────────────────────────────────────────

const HEARTBEAT_INTERVAL_MS = 25_000;

export function usePresence(resourceKey: string | undefined): { others: PresenceViewer[] } {
  const [isHidden, setIsHidden] = useState(() => typeof document !== 'undefined' && document.hidden);

  useEffect(() => {
    const onVisibilityChange = () => setIsHidden(document.hidden);
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => document.removeEventListener('visibilitychange', onVisibilityChange);
  }, []);

  const { data } = useQuery({
    queryKey: ['presence', resourceKey],
    queryFn: () => api.presenceHeartbeat(resourceKey!),
    enabled: !!resourceKey && !isHidden,
    refetchInterval: HEARTBEAT_INTERVAL_MS,
    // Presence is a best-effort awareness signal, not core data — a
    // transient failure (e.g. a slow cold start) shouldn't spam retries or
    // surface an error state; it just tries again on the next interval.
    retry: false,
  });

  return { others: data?.others ?? [] };
}
